class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector('.search_details');
    this.predictiveSearch = this.querySelector('predictive-search')
    this.isArabic = document.documentElement.lang.toLowerCase().startsWith('ar');
    this.isOpen = false;

    this.setupEventListeners();
  }
  
  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));
    
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 500).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));
    if (this.closeStickySearchButton != null) {
      this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    
    if (!searchTerm.length) return this.showTrendingAndProducts();

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
        // this.hideTrendingAndProducts();
      };
    })
  }
  
  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.showTrendingAndProducts();
    };
    if (event.code === 'Escape') {
      event.preventDefault();
      this.close();
      this.input.focus();
      return;
    }
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown' || event.code === 'Enter') {
      event.preventDefault();
    }
    
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('[role="option"]');
    let activeElement = allElements[0];

    if (!activeElement || (moveUp && !selectedElement)) return;

    this.statusElement.textContent = '';
    
    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }
    
  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"]');
    const selectedProduct = selectedOption?.matches('a, button')
      ? selectedOption
      : selectedOption?.querySelector('a, button');

    if (selectedProduct) selectedProduct.click();
  }
  
  getSearchResults(searchTerm) {
    if (this.isMegaPredictiveQuery(searchTerm) && this.querySelector('[data-bk-final360-mega-predictive-card]')) {
      if (this.renderMegaResults(searchTerm)) return;
    }

    if (this.isArabic) {
      this.renderArabicFullSearchFallback(searchTerm);
      return;
    }

    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();
    
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.updateViewAllLink(searchTerm);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=${this.productToShow}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        
        return response.text();
      })  
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
        this.updateViewAllLink(searchTerm);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  isMegaPredictiveQuery(searchTerm) {
    const normalizedTerm = searchTerm.toLowerCase().replace(/\s+/g, ' ').trim();
    return [
      'juicy bloom',
      'mega',
      'mango vanilla',
      'strawberry vanilla',
      'lip gloss',
      'gloss'
    ].some((term) => normalizedTerm.includes(term));
  }

  renderMegaResults(searchTerm) {
    const cards = Array.from(this.querySelectorAll('[data-bk-final360-mega-predictive-card]'))
      .map((template) => template.content.firstElementChild?.cloneNode(true).outerHTML)
      .filter(Boolean)
      .join('');

    if (!cards) {
      if (this.isArabic) this.renderArabicFullSearchFallback(searchTerm);
      return this.isArabic;
    }

    const fullSearchUrl = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product`;
    const resultsHeading = this.isArabic ? 'نتائج البحث' : 'Search results';
    const viewAllLabel = this.isArabic ? 'عرض كل النتائج' : 'View all results';
    const resultsStatus = this.isArabic ? 'تم العثور على نتيجتين من ميجا' : '2 Mega results found';
    this.predictiveSearchResults.innerHTML = `
      <div id="predictive-search-results" class="quickSearchResults quickSearchResultsBlock custom-scrollbar">
        <div class="container">
          <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing search-block-title text-left">
            <span class="text">${resultsHeading}</span>
          </h2>
          <div id="predictive-search-results-list" class="predictive-search__results-list list-unstyled" role="listbox" aria-labelledby="predictive-search-products">
            <div class="products-grid column-${this.productToShow} disable-srollbar text-left">${cards}</div>
            <div class="predictive-search__list-item productViewAll" role="option">
              <a href="${fullSearchUrl}" class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow button button-view-all" tabindex="-1">
                ${viewAllLabel}
              </a>
            </div>
          </div>
          <span class="hidden" data-predictive-search-live-region-count-value>${resultsStatus}</span>
        </div>
      </div>`;
    this.ensureOptionIds();
    this.input.setAttribute('aria-activedescendant', '');
    this.setAttribute('results', true);
    this.setAttribute('open', true);
    this.open();
    this.hideTrendingAndProducts();
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.setLiveRegionText(resultsStatus);
    return true;
  }

  renderArabicFullSearchFallback(searchTerm) {
    const fullSearchUrl = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product`;
    this.predictiveSearchResults.innerHTML = `
      <div id="predictive-search-results" class="quickSearchResults quickSearchResultsBlock custom-scrollbar">
        <div class="container">
          <div id="predictive-search-results-list" class="predictive-search__results-list list-unstyled" role="listbox" aria-label="نتائج البحث">
            <div class="productEmpty text-center animate-arrow" role="option">
              <a class="icon-arrow search-more" data-arabic-full-search href="${fullSearchUrl}">
                <span class="text">اعرضي نتائج البحث الكاملة عن <strong data-arabic-search-term></strong></span>
              </a>
            </div>
          </div>
          <span class="hidden" data-predictive-search-live-region-count-value>استخدمي البحث الكامل لعرض النتائج</span>
        </div>
      </div>`;
    this.predictiveSearchResults.querySelector('[data-arabic-search-term]').textContent = `“${searchTerm}”`;
    this.ensureOptionIds();
    this.input.setAttribute('aria-activedescendant', '');
    this.setAttribute('results', true);
    this.setAttribute('open', true);
    this.open();
    this.hideTrendingAndProducts();
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.setLiveRegionText('استخدمي البحث الكامل لعرض النتائج');
  }
  
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.ensureOptionIds();
    this.setAttribute('results', true);
    this.setAttribute('open', true);
    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();
  }

  ensureOptionIds() {
    const inputId = (this.input.id || 'predictive-search').replace(/[^a-zA-Z0-9_-]/g, '-');
    this.querySelectorAll('[role="option"]').forEach((option, index) => {
      if (!option.id) option.id = `${inputId}-option-${index + 1}`;
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  
  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('[class^="header-navigation"]').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
  
  open() {
    //this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    if (this.searchDetails) this.searchDetails.setAttribute('open', '');
    this.quickSearchWrapper = this.quickSearchWrapper || this.predictiveSearchResults.parentElement?.closest('.quickSearchResultsWrap');
    if (this.quickSearchWrapper) {
      this.quickSearchWrapper.style.display = 'block';
      this.quickSearchWrapper.classList.add('is-show');
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    if (this.searchDetails) this.searchDetails.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.quickSearchWrapper = this.quickSearchWrapper || this.predictiveSearchResults.parentElement?.closest('.quickSearchResultsWrap');
    if (this.quickSearchWrapper) {
      this.quickSearchWrapper.style.display = 'none';
      this.quickSearchWrapper.classList.remove('is-show');
    }

    this.isOpen = false;
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 
    if (this.searchDetails) this.searchDetails.setAttribute('open', '');
    this.quickSearchWrapper = this.quickSearchWrapper || this.predictiveSearchResults.parentElement?.closest('.quickSearchResultsWrap');
    if (this.quickSearchWrapper) {
      this.quickSearchWrapper.style.display = 'block';
      this.quickSearchWrapper.classList.add('is-show');
    }
    this.trendingAndProductsBlock.classList.add('is-show')
    this.trendingAndProductsBlock.classList.remove('hidden');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 

    this.trendingAndProductsBlock.classList.remove('is-show')
    this.trendingAndProductsBlock.classList.add('hidden');
  }

  onDocClick(e) {
    const isInModal = this.contains(e.target), $target = e.target

    if (!isInModal || $target.closest('.header-search-popup-close') || $target.matches('.header-search-popup-close') || $target.closest('.header-search-close') || $target.matches('.header-search-close')) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();
    document.querySelector('body').classList.remove('sticky-search-open');
    document.querySelector('body').classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-open')
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-custom-open')
  }

  updateViewAllLink(searchTerm) {
    const qsViewAllLink = document.querySelector('[data-qs-view-all-link]');
    if (!qsViewAllLink) return 

    const linkTotal = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product`
    
    qsViewAllLink.href = linkTotal
    if (this.isArabic) return;
    return this.getTotalResults(linkTotal).then(count => {
      qsViewAllLink.innerHTML = qsViewAllLink.innerHTML.replace('()', `(${count})`)
    });
  }

  getTotalResults(url) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })  
      .then((text) => {
        const totalCount = new DOMParser().parseFromString(text, 'text/html').querySelector('[id^="SearchSection"]').dataset.searchCount;
        return totalCount
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }
}

customElements.define('predictive-search', PredictiveSearch);
